import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTags, createTag, updateTag, deleteTag } from '../api/tags';
import { TagResponse } from '../types';
import { Chip, TAG_COLORS } from '../components/Chip';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PageHeader } from '../components/PageHeader';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';

function ColorSwatch({ color, selected, onClick }: { color: string | null; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`inline-flex rounded transition-opacity focus-visible:outline-none focus-visible:[&>span]:ring-2 focus-visible:[&>span]:ring-current ${
        selected ? '[&>span]:ring-[1.5px] [&>span]:ring-current' : 'opacity-60 hover:opacity-100'
      }`}
      title={color ?? 'auto'}
    >
      <Chip label={color ?? 'auto'} color={color} />
    </button>
  );
}

export function TagsPage() {
  const qc = useQueryClient();
  const [creatingName, setCreatingName] = useState('');
  const [creatingColor, setCreatingColor] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: getTags,
  });

  const createMutation = useMutation({
    mutationFn: () => createTag({ name: creatingName.trim().toLowerCase(), color: creatingColor }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags'] });
      setCreatingName('');
      setCreatingColor(null);
      setError(null);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'Failed to create tag.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; name: string; color: string | null }) =>
      updateTag(vars.id, { name: vars.name, color: vars.color }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags'] });
      qc.invalidateQueries({ queryKey: ['flows'] });
      setEditingId(null);
      setError(null);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'Failed to update tag.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTag,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags'] });
      qc.invalidateQueries({ queryKey: ['flows'] });
    },
  });

  function startEdit(tag: TagResponse) {
    setEditingId(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color);
    setError(null);
  }

  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4">
      <PageHeader title="Tags" description="Labels for grouping and filtering flows." />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">New tag</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="text"
              value={creatingName}
              onChange={e => setCreatingName(e.target.value)}
              placeholder="tag name"
              className="h-9 w-56"
              onKeyDown={e => { if (e.key === 'Enter' && creatingName.trim()) createMutation.mutate(); }}
            />
            <div className="flex flex-wrap gap-1 items-center">
              <ColorSwatch color={null} selected={creatingColor === null} onClick={() => setCreatingColor(null)} />
              {TAG_COLORS.map(c => (
                <ColorSwatch key={c} color={c} selected={creatingColor === c} onClick={() => setCreatingColor(c)} />
              ))}
            </div>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!creatingName.trim() || createMutation.isPending}
            >
              Create
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Color</TableHead>
            <TableHead className="text-right">Flows</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tags.map(t => {
            const editing = editingId === t.id;
            return (
              <TableRow key={t.id}>
                <TableCell>
                  {editing ? (
                    <Input
                      type="text"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      className="h-8 w-48"
                      autoFocus
                    />
                  ) : (
                    <Chip label={t.name} color={t.color} />
                  )}
                </TableCell>
                <TableCell>
                  {editing ? (
                    <div className="flex flex-wrap gap-1 items-center">
                      <ColorSwatch color={null} selected={editColor === null} onClick={() => setEditColor(null)} />
                      {TAG_COLORS.map(c => (
                        <ColorSwatch key={c} color={c} selected={editColor === c} onClick={() => setEditColor(c)} />
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">{t.color ?? 'auto'}</span>
                  )}
                </TableCell>
                <TableCell className="text-right text-muted-foreground">{t.flowCount}</TableCell>
                <TableCell>
                  <div className="flex gap-2 justify-end">
                    {editing ? (
                      <>
                        <Button
                          size="xs"
                          onClick={() => updateMutation.mutate({ id: t.id, name: editName.trim().toLowerCase(), color: editColor })}
                          disabled={!editName.trim() || updateMutation.isPending}
                        >
                          Save
                        </Button>
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() => { setEditingId(null); setError(null); }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="outline" size="xs" onClick={() => startEdit(t)}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="xs"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => {
                            const msg = t.flowCount > 0
                              ? `Delete tag "${t.name}"? It will be removed from ${t.flowCount} flow${t.flowCount === 1 ? '' : 's'}.`
                              : `Delete tag "${t.name}"?`;
                            if (window.confirm(msg)) deleteMutation.mutate(t.id);
                          }}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {tags.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="p-6 text-center text-muted-foreground">
                No tags yet. Create one above or add a tag to a flow.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
